import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Building2, Shield, Check, X, HelpCircle, StickyNote, Send, Copy, Search } from "lucide-react";

const STATUSES = [["pending", "Pending"], ["more_info", "More Info"], ["approved", "Approved"], ["rejected", "Rejected"]];
const ROLE_LABEL = { owner: "Organization Owner", director: "Director", general_manager: "General Manager", head_coach: "Head Coach", assistant_coach: "Assistant Coach", team_manager: "Team Manager", other: "Other" };

export default function AdminClaims() {
  const [status, setStatus] = useState("pending");
  const [claims, setClaims] = useState(null);

  const load = () => api.get("/live/admin/claims", { params: status ? { status } : {} }).then(({ data }) => setClaims(data)).catch(() => setClaims([]));
  useEffect(() => { load(); }, [status]);

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl" data-testid="admin-claims">
        <div className="label-overline text-[#E63946] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> CBR Live</div>
        <h1 data-testid="admin-claims-title" className="font-display text-4xl uppercase tracking-tighter mt-3">Ownership Claims</h1>
        <p className="text-sm text-slate-600 mt-2">Review organization &amp; team ownership requests, or invite an organization to claim theirs.</p>

        <InvitePanel />

        <div className="flex gap-1 mt-10 mb-4 border-b border-slate-200">
          {STATUSES.map(([v, l]) => (
            <button key={v} data-testid={`claims-tab-${v}`} onClick={() => setStatus(v)}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 ${status === v ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400"}`}>{l}</button>
          ))}
        </div>

        {!claims ? <div className="py-16 text-center text-slate-400">Loading…</div>
          : claims.length === 0 ? <div className="bg-white border border-dashed border-slate-200 rounded-sm py-16 text-center text-slate-400">No {status.replace("_", " ")} claims.</div>
          : <div className="space-y-4">{claims.map((c) => <ClaimCard key={c.id} c={c} onChange={load} />)}</div>}
      </div>
    </AdminLayout>
  );
}

function ClaimCard({ c, onChange }) {
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const s = c.context?.summary || {};
  const v = c.context?.verification || {};
  const isOpen = c.status === "pending" || c.status === "more_info";

  const act = (path, body) => api.post(`/live/admin/claims/${c.id}/${path}`, body || {})
    .then(() => { toast.success("Updated"); onChange(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  const addNote = () => { if (!note.trim()) return; api.post(`/live/admin/claims/${c.id}/notes`, { text: note }).then(() => { setNote(""); toast.success("Note added"); onChange(); }); };

  const domainBadge = { match: "bg-emerald-100 text-emerald-700", different: "bg-amber-100 text-amber-700", unknown: "bg-slate-100 text-slate-500" }[v.domain_match] || "bg-slate-100 text-slate-500";
  const domainLabel = { match: "✓ Domain match", different: "⚠ Different domain", unknown: "Domain unknown" }[v.domain_match];

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`claim-card-${c.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-[#0A192F] grid place-items-center shrink-0">
          {c.target_type === "organization" ? <Building2 className="w-5 h-5 text-[#E63946]" /> : <Shield className="w-5 h-5 text-[#E63946]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg uppercase tracking-tight">{c.target_name}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">{c.target_type}</span>
            <StatusPill status={c.status} />
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="label-overline mb-1">Request</div>
          <div className="text-sm space-y-0.5">
            <div><b>{c.requester_name}</b> · {ROLE_LABEL[c.requester_role] || c.requester_role}</div>
            <div className="text-slate-500">{c.requester_email}{c.requester_phone ? ` · ${c.requester_phone}` : ""}</div>
            <div className="text-slate-600 italic mt-1">"{c.message}"</div>
          </div>
          <div className="mt-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${domainBadge}`} data-testid={`claim-domain-${c.id}`}>{domainLabel}</span>
            {v.website && <span className="text-[11px] text-slate-400 ml-2">{v.website}</span>}
          </div>
        </div>
        <div>
          <div className="label-overline mb-1">Existing {c.target_type}</div>
          {c.context?.target_missing ? <div className="text-sm text-amber-600">Target no longer exists.</div> : (
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Teams", s.teams], ["Players", s.players], ["Games", s.games]].map(([k, val]) => (
                <div key={k} className="bg-slate-50 rounded-sm py-2"><div className="font-data text-lg text-[#0A192F]">{val ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div></div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-2 capitalize">Status: {(s.management_status || "").replace("_", " ") || "—"}{s.current_owner ? ` · Owner: ${s.current_owner}` : " · No owner"}</div>
          {s.slug && c.target_type === "team" && <Link to={`/live/teams/${s.slug}`} className="text-[11px] font-semibold text-[#E63946]">View profile →</Link>}
        </div>
      </div>

      {/* Admin notes (CBR only) */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="label-overline flex items-center gap-1 mb-1"><StickyNote className="w-3.5 h-3.5 text-[#E63946]" /> CBR Admin Notes <span className="text-slate-300 normal-case">(internal only)</span></div>
        {(c.admin_notes || []).length > 0 && (
          <ul className="space-y-1 mb-2" data-testid={`claim-notes-${c.id}`}>
            {c.admin_notes.map((n, i) => <li key={i} className="text-[12px] text-slate-600">• {n.text} <span className="text-slate-300">— {n.by_name}, {new Date(n.at).toLocaleDateString()}</span></li>)}
          </ul>
        )}
        <div className="flex gap-2">
          <input data-testid={`claim-note-input-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="flex-1 border border-slate-200 rounded-sm px-2 py-1.5 text-sm" />
          <button data-testid={`claim-note-btn-${c.id}`} onClick={addNote} className="text-xs font-semibold text-[#0A192F] border border-slate-200 px-3 rounded-sm">Add</button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Optional message to requester (for reject / request info)" className="w-full border border-slate-200 rounded-sm px-2 py-1.5 text-sm mb-2" />
          <div className="flex flex-wrap gap-2">
            <button data-testid={`approve-claim-${c.id}`} onClick={() => act("approve", { message: msg })} className="flex items-center gap-1.5 bg-[#E63946] text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-sm"><Check className="w-3.5 h-3.5" /> Approve</button>
            <button data-testid={`reject-claim-${c.id}`} onClick={() => act("reject", { message: msg })} className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-sm"><X className="w-3.5 h-3.5" /> Reject</button>
            <button data-testid={`request-info-claim-${c.id}`} onClick={() => act("request-info", { message: msg })} className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-sm"><HelpCircle className="w-3.5 h-3.5" /> Request Info</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InvitePanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [target, setTarget] = useState(null);
  const [email, setEmail] = useState("");
  const [link, setLink] = useState("");

  const search = () => api.get("/live/claimable/search", { params: { q } }).then(({ data }) => setResults([...(data.organizations || []), ...(data.teams || [])])).catch(() => setResults([]));
  const create = () => {
    if (!target) return toast.error("Pick a target");
    api.post("/live/admin/claim-invites", { target_type: target.target_type, target_id: target.id, recipient_email: email || null })
      .then(({ data }) => { setLink(data.claim_link); toast.success("Invite link created"); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  };
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Link copied"); };

  return (
    <div className="bg-[#0A192F] text-white rounded-sm p-5 mt-6" data-testid="invite-panel">
      <div className="label-overline text-[#E63946] flex items-center gap-2"><Send className="w-4 h-4" /> Invite an Organization to Claim</div>
      <p className="text-xs text-white/50 mt-1">CBR-initiated invites grant instant ownership on accept — no further approval needed.</p>
      <div className="flex gap-2 mt-3">
        <input data-testid="invite-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search org or team…" className="flex-1 rounded-sm px-3 py-2 text-sm text-[#0A192F]" />
        <button data-testid="invite-search-btn" onClick={search} className="bg-white/10 px-4 rounded-sm flex items-center gap-1.5 text-sm"><Search className="w-4 h-4" /> Search</button>
      </div>
      {results && !target && (
        <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
          {results.length === 0 ? <div className="text-xs text-white/40 py-2">No matches.</div> : results.map((r) => (
            <button key={`${r.target_type}-${r.id}`} data-testid={`invite-result-${r.id}`} onClick={() => { setTarget(r); setResults(null); }} className="w-full text-left px-3 py-2 rounded-sm hover:bg-white/5 text-sm flex items-center gap-2">
              {r.target_type === "organization" ? <Building2 className="w-4 h-4 text-white/40" /> : <Shield className="w-4 h-4 text-white/40" />}
              <span className="flex-1 truncate">{r.name}</span><span className="text-[10px] text-white/40 capitalize">{r.target_type}</span>
            </button>
          ))}
        </div>
      )}
      {target && (
        <div className="mt-3">
          <div className="flex items-center gap-2 text-sm mb-2"><span className="font-semibold">{target.name}</span><span className="text-[10px] text-white/40 capitalize">{target.target_type}</span><button onClick={() => setTarget(null)} className="text-xs text-white/50 ml-auto">change</button></div>
          <div className="flex gap-2">
            <input data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Recipient email (optional)" className="flex-1 rounded-sm px-3 py-2 text-sm text-[#0A192F]" />
            <button data-testid="create-invite-btn" onClick={create} className="bg-[#E63946] px-4 rounded-sm text-sm font-semibold uppercase tracking-wider">Create Link</button>
          </div>
        </div>
      )}
      {link && (
        <div className="mt-3 flex items-center gap-2 bg-white/10 rounded-sm p-2" data-testid="invite-link-box">
          <span className="text-xs truncate flex-1">{link}</span>
          <button data-testid="copy-invite-link-btn" onClick={copy} className="flex items-center gap-1 text-xs font-semibold bg-white text-[#0A192F] px-3 py-1.5 rounded-sm"><Copy className="w-3.5 h-3.5" /> Copy</button>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = { pending: "bg-amber-100 text-amber-700", more_info: "bg-blue-100 text-blue-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-slate-100 text-slate-500" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${map[status] || map.pending}`}>{status.replace("_", " ")}</span>;
}
